import { createTheme } from '@mui/material/styles';

/**
 * Colour values live in `@repo/design-system/styles.css` as CSS custom properties, not
 * here. `nativeColor` lets MUI consume them directly and derive its own hover, focus,
 * and selected states in CSS, so a `data-theme` swap restyles MUI through the cascade
 * with no JavaScript — no re-resolving, no theme rebuild, no stale frame.
 *
 * Two consequences worth knowing before editing this file:
 *
 * 1. `light` and `dark` are pinned to explicit tokens rather than left for MUI to
 *    derive. MUI would otherwise generate them with `color-mix()` in OKLCH, which
 *    lands slightly off the sRGB values it produced before; pinning keeps every
 *    legacy screen exactly where it was.
 * 2. `contrastText` is deliberately not pinned. MUI computes it with CSS relative
 *    colour, which adapts automatically across theme swaps.
 *
 * The palette reads `--brand-*`, not the semantic roles. That bridge keeps unmigrated
 * MUI screens looking like CoPlan while migrated surfaces adopt the new system; see
 * the comment in design-system.css.
 */
export const theme = createTheme({
  cssVariables: { nativeColor: true },

  palette: {
    mode: 'light',
    primary: {
      main: 'var(--brand-primary)',
      light: 'var(--brand-primary-light)',
      dark: 'var(--brand-primary-dark)',
    },
    secondary: {
      main: 'var(--brand-secondary)',
      light: 'var(--brand-secondary-light)',
      dark: 'var(--brand-secondary-dark)',
    },
    info: {
      main: 'var(--brand-info)',
      light: 'var(--brand-info-light)',
      dark: 'var(--brand-info-dark)',
    },
    error: {
      main: 'var(--brand-error)',
      light: 'var(--brand-error-light)',
      dark: 'var(--brand-error-dark)',
    },
    warning: {
      main: 'var(--brand-warning)',
      light: 'var(--brand-warning-light)',
      dark: 'var(--brand-warning-dark)',
    },
    success: {
      main: 'var(--brand-success)',
      light: 'var(--brand-success-light)',
      dark: 'var(--brand-success-dark)',
    },
    background: { default: 'var(--brand-background)', paper: 'var(--brand-surface)' },
    text: { primary: 'var(--brand-text-primary)', secondary: 'var(--brand-text-secondary)' },
    divider: 'var(--brand-divider)',
  },

  shape: { borderRadius: 12 },
  shadows: [
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
  ],

  typography: {
    fontFamily: `"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`,
    h1: { fontWeight: 700, fontSize: '3rem', lineHeight: 1.2, letterSpacing: '-0.025em' },
    h2: { fontWeight: 600, fontSize: '2.5rem', lineHeight: 1.3, letterSpacing: '-0.025em' },
    h3: { fontWeight: 600, fontSize: '1.5rem', lineHeight: 1.4 },
    h4: { fontWeight: 600, fontSize: '1.25rem', lineHeight: 1.4 },
    body1: { fontSize: '1.1rem', lineHeight: 1.6 },
    body2: { fontSize: '0.95rem', lineHeight: 1.5 },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        'html, body, #root': {
          background: `linear-gradient(135deg, #fafbfc 0%, #f8fafc 25%, #f1f5f9 50%, #e2e8f0 100%)`,
          backgroundAttachment: 'fixed',
          minHeight: '100vh',
        },
        // The Google Drive Picker ships unlayered CSS from gapi, which outranks every
        // cascade layer, so overriding it still needs `!important`.
        //
        // Moved off a hardcoded 1300 onto the shared scale: at 1300 it tied with our
        // own dialogs and resolved by DOM order, which is not something to leave to
        // chance for an overlay that opens *from* a dialog. `--z-drive-picker` sits
        // just above the modal tier and still below tooltips.
        '.picker-dialog-bg, .picker-dialog': {
          zIndex: 'var(--z-drive-picker) !important' as string,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: 8,
          padding: '10px 20px',
        },
        contained: {
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    //add scrolling to mui tabs
    MuiTabs: {
      defaultProps: {
        variant: 'scrollable',
      },
      styleOverrides: {
        root: {
          // Style overrides go here, not props
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          border: '1px solid #f3f4f6',
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          textAlign: 'center',
          '& .MuiCardHeader-title': {
            fontWeight: 600,
            fontSize: '1.75rem',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backdropFilter: 'blur(12px)',
          border: '1px solid var(--mui-palette-divider)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#1f2937',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        },
      },
    },
    // new inspector / drawer
    MuiDrawer: {
      styleOverrides: {
        paper: { borderRadius: '12px 0 0 12px' },
      },
    },
    // example presence avatar stack
    MuiAvatarGroup: {
      styleOverrides: {
        root: {
          '& .MuiAvatar-root': {
            border: `2px solid var(--mui-palette-background-paper)`,
          },
        },
      },
    },
  },
});

export default theme;
